import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
} from "@bw/shared/api";
import {
  useWaiverGate,
  AuthContext,
  useFrontendConfig,
  usePaymentMethods,
  useWalletSetupIntent,
  paymentQueryKeys,
} from "@bw/shared/hooks";
import type {
  BatchBookingRequest,
  BatchBookingResponse,
} from "@bw/shared/types/generated";
import { reconcileSavedPaymentMethodSelection } from "@bw/shared/utils";
import { Page } from "../../../common/components/Page";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
  MaterialPaymentMethodPicker,
  MaterialWaiverGate,
} from "../../../common/components/payment";
import { useCart } from "../state/cartStore";
import { useFacility } from "../hooks/useFacility";
import { useCourts } from "../hooks/useCourts";
import { useCartQuote } from "../hooks/useCartQuote";
import { useBatchBooking } from "../hooks/useBatchBooking";
import {
  clearBookingAttempt,
  loadBookingAttempt,
  storeBookingAttempt,
} from "../state/bookingAttemptStorage";
import { QuoteSummary } from "../components/QuoteSummary";
import { CampaignPicker } from "../components/CampaignPicker";
import { PlayerCountStepper } from "../components/PlayerCountStepper";
import { ConfirmBar } from "../components/ConfirmBar";
import { ConfirmationPage } from "./ConfirmationPage";

const DEFAULT_TZ = "America/Los_Angeles";

type Step = "quote" | "payment" | "confirm";

function isCampaignRejection(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.code === "CAMPAIGN_NOT_FOUND" ||
      error.code === "CAMPAIGN_NOT_ELIGIBLE")
  );
}

/**
 * Checkout orchestrator (slice 3b). Route /book/:facilityId/review.
 *
 * Three in-page steps over the LIFTED cart:
 *   1. QUOTE — the BINDING cart quote (per-slot + total + currency) from the
 *      backend. This replaces the 3a client-side estimate entirely; there is no
 *      client pricing on this screen.
 *   2. PAYMENT — pick a saved card (or add one via Stripe Elements scoped to the
 *      facility's connected account). Confirm is disabled until a pm_id exists.
 *   3. CONFIRM — generate a FRESH idempotency_key and call batchCreateBooking.
 *      Success → confirmation + clear cart. Errors map by status (no fake success).
 *
 * Zero client-side money math — every number shown is the server's quote/charge.
 */
export function ReviewPage() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const auth = useContext(AuthContext);
  const userId = auth?.user?.uid ?? null;
  return (
    <ScopedReviewPage
      key={`${facilityId ?? "missing"}:${userId ?? "signed-out"}`}
      facilityId={facilityId}
      userId={userId}
    />
  );
}

function ScopedReviewPage({
  facilityId,
  userId,
}: {
  facilityId: string | undefined;
  userId: string | null;
}) {
  const navigate = useNavigate();
  const discoveryReturn = readFacilityDiscoveryReturn(useLocation().search);
  const { items, clear } = useCart();
  const [initialAttempt] = useState<BatchBookingRequest | null>(() =>
    facilityId && userId ? loadBookingAttempt(facilityId, userId) : null,
  );
  const [isRecoveredAttempt, setIsRecoveredAttempt] = useState(
    initialAttempt !== null,
  );

  const facilityQ = useFacility(facilityId);
  // The REAL venue tz — undefined until the facility resolves. The quote and the
  // booking are priced/charged under this tz, so we must NOT send the Pacific
  // fallback before it loads (a non-Pacific venue would be mispriced, and the
  // quote cache key now includes tz). The quote is gated off it; the booking
  // confirm is too. DEFAULT_TZ is only a type-level fallback for request shaping.
  const resolvedTimezone = facilityQ.data?.timezone;
  const timezone = resolvedTimezone ?? DEFAULT_TZ;

  const [step, setStep] = useState<Step>(() =>
    initialAttempt ? "confirm" : "quote",
  );
  const paymentSelectionContext = `${facilityId ?? "missing"}:${userId ?? "signed-out"}`;
  const [paymentSelection, setPaymentSelection] = useState(() => ({
    context: paymentSelectionContext,
    paymentMethodId: null as string | null,
  }));
  const selectedPmId =
    paymentSelection.context === paymentSelectionContext
      ? paymentSelection.paymentMethodId
      : null;
  const selectPaymentMethod = useCallback(
    (paymentMethodId: string | null) => {
      setPaymentSelection({
        context: paymentSelectionContext,
        paymentMethodId,
      });
    },
    [paymentSelectionContext],
  );
  const initializedPaymentContextRef = useRef<string | null>(
    initialAttempt ? paymentSelectionContext : null,
  );
  // The SINGLE booking-level party size (slice ② allocation), applied to every
  // court slot. Defaults to 1 (today's behavior). The stepper only renders when
  // the facility's per_court_allocation flag is on; with the flag off this stays
  // 1 and the quote/booking behave exactly as before. NEVER exceeds the cap — it
  // is clamped to `min(playerCount, allocationCap)` before being sent anywhere.
  const [playerCount, setPlayerCount] = useState<number>(1);
  // The idempotency key is regenerated per genuinely-separate attempt (cart
  // change or a prior hard failure); stable only for an in-flight retry of the
  // identical request. Initialised lazily once the page mounts.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(
    () => initialAttempt?.idempotency_key ?? crypto.randomUUID(),
  );
  const [conflict, setConflict] = useState<string | null>(null);
  const [success, setSuccess] = useState<BatchBookingResponse | null>(null);
  const [attemptSnapshot, setAttemptSnapshot] =
    useState<BatchBookingRequest | null>(initialAttempt);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    initialAttempt?.campaign_id ?? null,
  );
  const [campaignOverrideId, setCampaignOverrideId] = useState<string | null>(
    initialAttempt?.campaign_id ?? null,
  );
  const [offersDisabled, setOffersDisabled] = useState(false);
  const [manualCampaignSelection, setManualCampaignSelection] = useState(
    initialAttempt?.campaign_id != null,
  );
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const operationGeneration = useRef(0);
  useEffect(
    () => () => {
      operationGeneration.current += 1;
    },
    [],
  );

  // ---- Player allocation (slice ②) ------------------------------------------
  // The stepper is GATED on the facility's per_court_allocation.enabled flag. The
  // cap is the TIGHTEST booked-court seat limit: min over the cart's courts of
  // (court.max_players ?? facility.max_count) — mirroring the backend's
  // `_resolve_allocation` min() semantics. We read the courts to compute it.
  const courtsQ = useCourts(facilityId);
  const allocation = facilityQ.data?.per_court_allocation;
  const allocationEnabled = allocation?.enabled === true;

  // Per-court cap = the court's own max_players, else the facility max_count,
  // else unbounded. The effective cap = the tightest (min) across booked courts.
  // Computed only over the courts actually in the cart (not every facility court).
  const courtsById = new Map(
    (courtsQ.data ?? []).map((c) => [c.court_id, c] as const),
  );
  const facilityMax = allocation?.max_count ?? null;
  const bookedCourtCaps = items.map((s) => {
    const court = courtsById.get(s.court_id);
    const courtMax = court?.max_players ?? null;
    return courtMax ?? facilityMax ?? Number.POSITIVE_INFINITY;
  });
  const allocationCap = bookedCourtCaps.length
    ? Math.min(...bookedCourtCaps)
    : Number.POSITIVE_INFINITY;

  // The count actually sent to the quote + batch is FENCED at the cap so an
  // over-cap value can never be quoted or charged, even if state somehow holds a
  // higher number (the stepper's `+` already disables at the cap; this is the
  // belt-and-braces clamp the cap tests pin). With the flag off, the count is a
  // flat 1 — the quote/booking are byte-for-byte today's behavior.
  const effectiveCount = allocationEnabled
    ? Math.min(playerCount, allocationCap)
    : 1;

  const quoteQ = useCartQuote(
    facilityId,
    items,
    resolvedTimezone,
    effectiveCount,
    campaignOverrideId,
    offersDisabled,
    attemptSnapshot === null,
  );
  useEffect(() => {
    const selectedByServer = quoteQ.data?.campaign?.campaign_id ?? null;
    if (
      attemptSnapshot === null &&
      !offersDisabled &&
      !manualCampaignSelection
    ) {
      setSelectedCampaignId(selectedByServer);
    }
  }, [
    attemptSnapshot,
    manualCampaignSelection,
    offersDisabled,
    quoteQ.data?.campaign?.campaign_id,
  ]);
  const isPaymentStep = step === "payment" || step === "confirm";
  const configQ = useFrontendConfig(facilityId, isPaymentStep);
  const methodsQ = usePaymentMethods(facilityId, isPaymentStep);
  const paymentMethodsSettled =
    isPaymentStep &&
    methodsQ.data !== undefined &&
    !methodsQ.isFetching &&
    !methodsQ.isError;
  useEffect(() => {
    if (attemptSnapshot !== null) {
      initializedPaymentContextRef.current = paymentSelectionContext;
      return;
    }
    if (!paymentMethodsSettled || methodsQ.data === undefined) return;

    const initializeProviderDefault =
      initializedPaymentContextRef.current !== paymentSelectionContext;
    initializedPaymentContextRef.current = paymentSelectionContext;
    setPaymentSelection((current) => {
      const currentId =
        current.context === paymentSelectionContext
          ? current.paymentMethodId
          : null;
      const paymentMethodId = reconcileSavedPaymentMethodSelection(
        methodsQ.data,
        currentId,
        initializeProviderDefault,
      );
      if (
        current.context === paymentSelectionContext &&
        current.paymentMethodId === paymentMethodId
      ) {
        return current;
      }
      return { context: paymentSelectionContext, paymentMethodId };
    });
  }, [
    attemptSnapshot,
    methodsQ.data,
    paymentMethodsSettled,
    paymentSelectionContext,
  ]);
  const chargeReadyPmId =
    paymentMethodsSettled && methodsQ.data !== undefined
      ? reconcileSavedPaymentMethodSelection(methodsQ.data, selectedPmId, false)
      : null;
  const setupIntent = useWalletSetupIntent();
  const batch = useBatchBooking(facilityId);

  // Per-person liability-waiver gate. The facility may require a signed waiver
  // before any court booking; the backend does not enforce it on this path yet
  // (#1316), so this client gate is the barrier. It FAILS CLOSED — "Confirm &
  // pay" stays disabled while the status read is loading or has errored, and
  // while the player has not accepted a required waiver.
  const waiver = useWaiverGate(facilityId ?? "");

  // A changed cart invalidates any prior attempt's key — never reuse a uuid
  // across a genuinely different request (would wrongly dedupe at the backend).
  const cartSig = items.map((s) => `${s.court_id}|${s.start_time}`).join(",");
  useEffect(() => {
    if (attemptSnapshot === null) {
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [attemptSnapshot, cartSig]);

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );

  const err = batch.error;
  const detectedPaymentAuthentication = getPaymentAuthenticationRequired(err);
  const detectedPaymentClientSecret =
    detectedPaymentAuthentication?.clientSecret ?? null;
  const [activePaymentAuthentication, setActivePaymentAuthentication] =
    useState<{ clientSecret: string } | null>(null);
  useEffect(() => {
    if (detectedPaymentClientSecret) {
      setActivePaymentAuthentication((current) =>
        current?.clientSecret === detectedPaymentClientSecret
          ? current
          : { clientSecret: detectedPaymentClientSecret },
      );
      return;
    }
    if (isTerminalPaymentAttemptError(err)) {
      setActivePaymentAuthentication(null);
    }
  }, [detectedPaymentClientSecret, err]);
  const paymentAuthentication =
    detectedPaymentAuthentication ?? activePaymentAuthentication;

  // ---- Success → confirmation -----------------------------------------------
  // ORDERING MATTERS: this guard MUST come BEFORE the empty-cart guard below.
  // A successful booking clears the cart (`onSuccess` runs `clear()` then
  // `setSuccess(res)`), so once `success` is set the cart is already empty. If
  // the empty-cart guard ran first it would short-circuit to "Your cart is
  // empty" and the player would never see the confirmation.
  if (success) {
    return <ConfirmationPage result={success} facilityId={facilityId ?? ""} />;
  }

  // ---- Empty cart — honest, no quote call -----------------------------------
  // Reached only when there is NO success yet (a genuinely empty cart). The
  // post-booking empty cart is handled by the success guard above.
  if (items.length === 0 && attemptSnapshot === null) {
    return (
      <Page title="Review & pay">
        <Alert
          severity="info"
          role="status"
          action={
            <Button
              type="button"
              color="inherit"
              size="small"
              onClick={() =>
                navigate(
                  withFacilityDiscoveryReturn(
                    `/book/${facilityId}`,
                    discoveryReturn,
                  ),
                )
              }
            >
              Back to court sheet
            </Button>
          }
        >
          Your cart is empty. Add some open court times to book.
        </Alert>
      </Page>
    );
  }

  // The quote is being recomputed when a count change (slice ②) has fired a fresh
  // /cart-quote that has not yet settled. `placeholderData: keepPreviousData`
  // keeps the PRIOR (stale) total on screen during that window, so authorizing
  // here would charge the NEW count against the OLD displayed price. Treat both
  // the placeholder flag (the prior count's total is still showing) and any
  // in-flight fetch as "recomputing" → the confirm CTA blocks and the displayed
  // total degrades to a pending state until the binding price for the count we
  // will charge has settled.
  const quoteRecomputing =
    attemptSnapshot === null && (quoteQ.isPlaceholderData || quoteQ.isFetching);
  const campaignAttemptRejected =
    selectedCampaignId !== null && isCampaignRejection(err);
  const campaignQuoteRejected =
    selectedCampaignId !== null && isCampaignRejection(quoteQ.error);
  const quoteUnavailable =
    quoteQ.isError ||
    !quoteQ.data ||
    campaignAttemptRejected ||
    (quoteQ.data.campaign?.campaign_id ?? null) !== selectedCampaignId;

  function persistAttempt(snapshot: BatchBookingRequest): void {
    if (facilityId && userId) {
      storeBookingAttempt(facilityId, userId, snapshot);
    }
  }

  function releasePersistedAttempt(snapshot: BatchBookingRequest): void {
    if (facilityId && userId) {
      clearBookingAttempt(facilityId, userId, snapshot);
    }
  }

  async function submitAttempt(
    snapshot: BatchBookingRequest,
    releaseInitialWaiverFailure = false,
  ): Promise<void> {
    const operationId = ++operationGeneration.current;
    setConflict(null);
    await batch.mutateAsync(snapshot, {
      onSuccess: (res) => {
        if (operationGeneration.current !== operationId) return;
        releasePersistedAttempt(snapshot);
        setIsRecoveredAttempt(false);
        clear();
        setSuccess(res);
      },
      onError: (error) => {
        if (operationGeneration.current !== operationId) return;
        // Only mint a FRESH key on a DEFINITIVE failure — an ApiError with a
        // 4xx status (402/422 declined, 409 conflict): the backend committed
        // nothing, so the next attempt is a genuinely separate request and
        // reusing the key would wrongly dedupe it to no charge.
        //
        // On an AMBIGUOUS outcome — a raw network/timeout error (a TypeError,
        // not an ApiError, so no status) OR a 5xx — the charge may have
        // ALREADY been applied at the backend but the response was lost.
        // Preserve the whole request snapshot and its key so the retry cannot
        // drift to a different card, cart, count, or timezone.
        const status = isApiError(error) ? error.status : null;
        if (
          isTerminalPaymentAttemptError(error) ||
          (status === 428 && releaseInitialWaiverFailure)
        ) {
          releasePersistedAttempt(snapshot);
          setIsRecoveredAttempt(false);
          setAttemptSnapshot(null);
          setIdempotencyKey(crypto.randomUUID());
        }
        if (status === 428) {
          void waiver.retryStatus();
        }
        if (snapshot.campaign_id && isCampaignRejection(error)) {
          setStep("quote");
        }
        if (
          status === 409 &&
          isTerminalPaymentAttemptError(error) &&
          !isCampaignRejection(error)
        ) {
          setConflict(
            "A slot is no longer available — please re-select your times.",
          );
        }
      },
    });
  }

  function handleConfirm() {
    if (attemptSnapshot !== null) {
      void submitAttempt(attemptSnapshot).catch(() => undefined);
      return;
    }
    // Fail closed: never submit while the waiver gate is blocking (loading,
    // errored, or a required-and-unaccepted waiver), without a payment method,
    // or before the venue tz resolves (the slots are charged under it — never
    // send the Pacific fallback).
    if (!chargeReadyPmId || waiver.isBlocked || !resolvedTimezone) return;
    // Defense in depth (the CTA is already disabled while recomputing): a click
    // that slips through MUST NOT charge a count whose binding price is still
    // being re-quoted — that is the displayed-vs-charged mismatch. Bail out and
    // let the settled-quote render re-enable confirm.
    if (quoteRecomputing || quoteUnavailable) return;
    const snapshot: BatchBookingRequest = {
      ...(selectedCampaignId ? { campaign_id: selectedCampaignId } : {}),
      slots: items.map((s) => ({
        court_id: s.court_id,
        start_time: s.start_time,
        end_time: s.end_time,
        timezone,
        // The single group size rides into EVERY slot (slice ②), fenced at the
        // cap so an over-cap count is never charged. With the flag off this is
        // a flat 1 — today's exact behavior.
        player_count: effectiveCount,
        // The whole group is priced as members on this player path (parity with
        // the cart quote's non_member_player_count: 0) — there is no non-member
        // distinction in the booking UI.
        non_member_player_count: 0,
      })),
      stripe_payment_method_id: chargeReadyPmId,
      idempotency_key: idempotencyKey,
    };
    persistAttempt(snapshot);
    setIsRecoveredAttempt(false);
    setAttemptSnapshot(snapshot);
    void submitAttempt(snapshot, true).catch(() => undefined);
  }

  // ---- Error mapping for the confirm step -----------------------------------
  const errStatus = isApiError(err) ? err.status : null;
  const waiverBlocked = errStatus === 428;
  const declined =
    paymentAuthentication === null && (errStatus === 402 || errStatus === 422);
  const authError = errStatus === 403;
  const paymentControlsLocked = attemptSnapshot !== null || cardSetupBusy;
  const hasAmbiguousOutcome =
    attemptSnapshot !== null &&
    (isRecoveredAttempt ||
      (batch.isError &&
        paymentAuthentication === null &&
        !isTerminalPaymentAttemptError(err)));

  // ---- Over-cap allocation guard (slice ②) ----------------------------------
  // A friendly court label for a court id, resolved from the booked-courts read
  // (the cart's court_name is a fallback). Never the raw id alone.
  function courtLabel(courtId: string): string {
    const fromCourts = courtsById.get(courtId)?.name?.trim();
    if (fromCourts) return fromCourts;
    const fromCart = items
      .find((s) => s.court_id === courtId)
      ?.court_name?.trim();
    return fromCart || "This court";
  }

  // The backend is the source of truth on the cap: a defensive over-cap
  // submission is rejected 409-style with code "PlayerCountExceedsCapacity" and
  // a message naming the court + its seat limit. We surface a CLEAR,
  // non-destructive message (court name + limit + how to fix) and BLOCK confirm —
  // we never fabricate a success on it. Parity with the existing 409/quote-error
  // handling. The message is parsed from the error; if the parse misses we still
  // show an honest generic over-cap message rather than a fake confirmation.
  const overCapError =
    isApiError(err) && err.code === "PlayerCountExceedsCapacity" ? err : null;
  const overCapMessage = overCapError
    ? (() => {
        const m = overCapError.message ?? "";
        const courtMatch = m.match(/court\s+([\w-]+)/i);
        const capMatch = m.match(/cap of\s+(\d+)/i);
        const courtId = courtMatch?.[1];
        const limit = capMatch?.[1];
        if (courtId && limit) {
          return `${courtLabel(courtId)} seats up to ${limit} — lower your group or remove that court.`;
        }
        return "One of your courts can't seat your whole group — lower your group or remove that court.";
      })()
    : null;

  return (
    <Page title="Review & pay">
      <Stack spacing={2.5}>
        {/* STEP 1 — BINDING quote ------------------------------------------- */}
        {step === "quote" ? (
          <Stack
            component="section"
            spacing={2}
            aria-label="Review your courts and price"
          >
            {/* Slice ② — the single party-size stepper, gated on the facility
                allocation flag. Rendered OUTSIDE the quote-state branch so it
                stays MOUNTED while a count change re-quotes (the count is in the
                quote key) — never unmounting the +/− the user is clicking. The
                surcharge surfaces in QuoteSummary below from the refreshed quote. */}
            {allocationEnabled ? (
              <PlayerCountStepper
                count={effectiveCount}
                cap={allocationCap}
                onStep={(delta) =>
                  // Functional updater + clamp: a rapid click run always steps
                  // from the LATEST count (never a stale render closure), and the
                  // result is fenced to [1, cap] so an over-cap value can never be
                  // reached, quoted, or charged.
                  setPlayerCount((prev) =>
                    Math.min(allocationCap, Math.max(1, prev + delta)),
                  )
                }
              />
            ) : null}

            {facilityId && resolvedTimezone ? (
              <CampaignPicker
                facilityId={facilityId}
                timezone={resolvedTimezone}
                selectedId={selectedCampaignId}
                offersDisabled={offersDisabled}
                onSelect={(campaignId) => {
                  setSelectedCampaignId(campaignId);
                  setCampaignOverrideId(campaignId);
                  setOffersDisabled(campaignId === null);
                  setManualCampaignSelection(true);
                  setConflict(null);
                  batch.reset();
                }}
              />
            ) : null}

            {campaignAttemptRejected || campaignQuoteRejected ? (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    onClick={() => {
                      setSelectedCampaignId(null);
                      setCampaignOverrideId(null);
                      setOffersDisabled(true);
                      setManualCampaignSelection(true);
                      setConflict(null);
                      batch.reset();
                    }}
                  >
                    Continue without this offer
                  </Button>
                }
              >
                This offer couldn't be used for this booking. Continue with the
                regular price or select another listed offer.
              </Alert>
            ) : !resolvedTimezone ||
              quoteRecomputing ||
              (quoteQ.isLoading && !quoteQ.data) ? (
              <CheckoutLoading label="Calculating your price…" />
            ) : quoteQ.isError ? (
              <QuoteError
                error={quoteQ.error}
                onRetry={() => quoteQ.refetch()}
              />
            ) : quoteQ.data ? (
              <>
                <QuoteSummary quote={quoteQ.data} slots={items} />
                <ConfirmBar
                  total={quoteQ.data.total}
                  currency={quoteQ.data.currency ?? "USD"}
                  actionLabel="Continue to payment"
                  onAction={() => setStep("payment")}
                  showTotal={false}
                  disabled={quoteUnavailable}
                />
              </>
            ) : null}
          </Stack>
        ) : null}

        {/* STEP 2/3 — payment + confirm ------------------------------------- */}
        {isPaymentStep ? (
          <Stack
            component="section"
            spacing={2}
            aria-label="Choose how you're paying"
          >
            {attemptSnapshot === null ? (
              <>
                {quoteRecomputing ? (
                  <CheckoutLoading label="Updating your price…" />
                ) : quoteQ.isError ? (
                  <QuoteError
                    error={quoteQ.error}
                    onRetry={() => void quoteQ.refetch()}
                  />
                ) : quoteQ.data && quoteQ.data.campaign ? (
                  <QuoteSummary quote={quoteQ.data} slots={items} />
                ) : null}
                <Button
                  size="small"
                  sx={{ alignSelf: "flex-start" }}
                  disabled={
                    attemptSnapshot !== null || batch.isPending || cardSetupBusy
                  }
                  onClick={() => {
                    setStep("quote");
                    setConflict(null);
                    batch.reset();
                  }}
                >
                  Change offer
                </Button>
              </>
            ) : null}
            {configQ.isError ? (
              <Alert severity="error" role="alert">
                This facility can&apos;t take card payments yet. Please try
                again later.
              </Alert>
            ) : methodsQ.isError &&
              !(paymentAuthentication && attemptSnapshot) ? (
              <PaymentMethodsLoadError
                retrying={methodsQ.isFetching}
                onRetry={() => void methodsQ.refetch()}
              />
            ) : configQ.isLoading || methodsQ.isLoading ? (
              <CheckoutLoading label="Loading payment options…" />
            ) : (
              <>
                <Box
                  component="fieldset"
                  aria-label="Saved card controls"
                  disabled={paymentControlsLocked}
                  sx={{ display: "contents" }}
                >
                  <MaterialPaymentMethodPicker
                    methods={methodsQ.data ?? []}
                    selectedId={
                      attemptSnapshot?.stripe_payment_method_id ?? selectedPmId
                    }
                    onSelect={selectPaymentMethod}
                    disabled={paymentControlsLocked || batch.isPending}
                    allowRemove
                    // The route guarantees facilityId; "" only satisfies the
                    // coupled prop type — the picker hides the remove
                    // affordance entirely on a falsy facility scope.
                    facilityId={facilityId ?? ""}
                    paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
                      facilityId ?? "",
                    )}
                  />
                </Box>

                {configQ.data ? (
                  <MaterialConnectedElementsProvider config={configQ.data}>
                    <Box
                      component="fieldset"
                      aria-label="Add card controls"
                      disabled={paymentControlsLocked}
                      sx={{ display: "contents" }}
                    >
                      <MaterialAddCardForm
                        publishableKey={configQ.data.publishable_key}
                        createSetupIntent={createSetupIntentSecret}
                        onBusyChange={setCardSetupBusy}
                        onCardAdded={async (pmId) => {
                          const refreshed = await methodsQ.refetch();
                          const savedMethod = refreshed.data?.find(
                            (method) => method.payment_method_id === pmId,
                          );
                          if (
                            refreshed.isError ||
                            savedMethod?.is_usable !== true
                          ) {
                            throw new Error(
                              "The saved card is not available yet. Retry to refresh it.",
                            );
                          }
                          selectPaymentMethod(pmId);
                        }}
                      />
                    </Box>
                    {paymentAuthentication && attemptSnapshot ? (
                      <Box
                        component="fieldset"
                        aria-label="Payment authentication controls"
                        disabled={waiver.isBlocked}
                        sx={{ display: "contents" }}
                      >
                        <MaterialPaymentIntentAuthentication
                          clientSecret={paymentAuthentication.clientSecret}
                          onAuthenticated={async (
                            authenticatedClientSecret,
                          ) => {
                            if (
                              paymentAuthentication.clientSecret !==
                              authenticatedClientSecret
                            ) {
                              return;
                            }
                            await submitAttempt(attemptSnapshot);
                          }}
                        />
                      </Box>
                    ) : null}
                  </MaterialConnectedElementsProvider>
                ) : null}

                {conflict ? (
                  <Alert
                    severity="error"
                    role="alert"
                    action={
                      <Button
                        type="button"
                        color="inherit"
                        size="small"
                        onClick={() =>
                          navigate(
                            withFacilityDiscoveryReturn(
                              `/book/${facilityId}`,
                              discoveryReturn,
                            ),
                          )
                        }
                      >
                        Back to court sheet
                      </Button>
                    }
                  >
                    {conflict}
                  </Alert>
                ) : null}
                {overCapMessage ? (
                  <Alert severity="error" role="alert">
                    {overCapMessage}
                  </Alert>
                ) : null}
                {waiverBlocked ? (
                  <Alert severity="error" role="alert">
                    This facility requires a signed waiver before you can book.
                  </Alert>
                ) : null}
                {declined ? (
                  <Alert severity="error" role="alert">
                    Your card was declined. Try a different card and confirm
                    again.
                  </Alert>
                ) : null}
                {authError ? (
                  <Alert severity="error" role="alert">
                    You&apos;re not authorized to complete this booking.
                  </Alert>
                ) : null}
                {hasAmbiguousOutcome ? (
                  <Alert severity="error" role="alert">
                    We couldn&apos;t confirm whether your booking finished. Use
                    Finish booking to retry the same booking request without
                    changing its payment details.
                  </Alert>
                ) : null}

                {step === "payment" ? (
                  <ConfirmBar
                    total={
                      quoteRecomputing || quoteUnavailable
                        ? null
                        : quoteQ.data?.total
                    }
                    pending={quoteRecomputing}
                    currency={quoteQ.data?.currency ?? "USD"}
                    actionLabel="Review order"
                    onAction={() => setStep("confirm")}
                    disabled={
                      !chargeReadyPmId ||
                      cardSetupBusy ||
                      quoteRecomputing ||
                      quoteUnavailable
                    }
                  />
                ) : (
                  <>
                    {/* Per-person waiver gate on the confirm step. The gate
                        FAILS CLOSED: while the status read is loading or has
                        errored, isBlocked is true and the CTA stays disabled —
                        but the accept control only renders once status RESOLVES
                        to "waiver required, not accepted" (there is no confirmed
                        version to accept while loading/errored). On a status-read
                        ERROR we keep the CTA disabled (fail-closed) but surface
                        an HONEST "couldn't confirm your waiver" block + Retry so
                        the player is never stranded on a silent dead button —
                        parity with the drop-in path's statusError branch. */}
                    {waiver.statusError ? (
                      <Alert
                        severity="error"
                        role="alert"
                        action={
                          <Button
                            type="button"
                            color="inherit"
                            size="small"
                            onClick={waiver.retryStatus}
                          >
                            Retry
                          </Button>
                        }
                      >
                        We couldn&apos;t confirm your waiver, so we can&apos;t
                        complete your booking yet. Please retry.
                      </Alert>
                    ) : waiver.isBlocked && !waiver.isLoading ? (
                      <MaterialWaiverGate
                        currentWaiver={waiver.currentWaiver}
                        accepting={waiver.isAccepting}
                        acceptError={Boolean(waiver.acceptError)}
                        onAccept={waiver.accept}
                      />
                    ) : waiver.currentVersionId !== null ? (
                      // ACCEPTED (or a waiver exists and is already satisfied):
                      // keep a compact, PERSISTENT "✓ Waiver accepted — Review
                      // waiver" affordance MOUNTED so the waiver text stays
                      // re-readable on this page after acceptance instead of
                      // vanishing. Reached only when NOT blocked (so the version
                      // is accepted) and the facility HAS a published waiver
                      // (currentVersionId != null) — no affordance when there is
                      // no waiver to review.
                      <MaterialWaiverGate
                        accepted
                        currentWaiver={waiver.currentWaiver}
                        accepting={waiver.isAccepting}
                        acceptError={Boolean(waiver.acceptError)}
                        onAccept={waiver.accept}
                      />
                    ) : null}

                    <ConfirmBar
                      // While a count-change re-quote is in flight the prior
                      // (stale) total is still in quoteQ.data — never present it
                      // as the final figure at the authorize moment. Pass null so
                      // ConfirmBar shows "Calculating…" instead of the old number;
                      // the settled total returns once the binding quote resolves.
                      total={
                        attemptSnapshot !== null ||
                        quoteRecomputing ||
                        quoteUnavailable
                          ? null
                          : quoteQ.data?.total
                      }
                      pending={!isRecoveredAttempt && quoteRecomputing}
                      showTotal={attemptSnapshot === null}
                      currency={quoteQ.data?.currency ?? "USD"}
                      actionLabel={
                        hasAmbiguousOutcome ? "Finish booking" : "Confirm & pay"
                      }
                      onAction={handleConfirm}
                      // Block authorizing a price that is being recomputed — you
                      // can only confirm the settled total for the count charged.
                      disabled={
                        (attemptSnapshot === null && !chargeReadyPmId) ||
                        (attemptSnapshot === null && quoteUnavailable) ||
                        waiver.isBlocked ||
                        (!isRecoveredAttempt && quoteRecomputing) ||
                        paymentAuthentication !== null ||
                        cardSetupBusy
                      }
                      busy={batch.isPending}
                    />
                  </>
                )}
              </>
            )}
          </Stack>
        ) : null}
      </Stack>
    </Page>
  );
}

function QuoteError({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  const mixedCurrency = isApiError(error) && error.status === 409;
  const rejectedCampaign = isCampaignRejection(error);
  return (
    <Alert
      severity="error"
      role="alert"
      action={
        <Button type="button" color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      {rejectedCampaign
        ? "This offer couldn't be applied to your cart. Choose another offer or No offer, or retry."
        : mixedCurrency
          ? "Your cart mixes currencies — remove slots so they share one currency."
          : "We couldn't get your price right now."}
    </Alert>
  );
}

function CheckoutLoading({ label }: { label: string }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      role="status"
      aria-live="polite"
      sx={{ py: 3, alignItems: "center", justifyContent: "center" }}
    >
      <CircularProgress size={22} />
      <Typography color="text.secondary">{label}</Typography>
    </Stack>
  );
}
